import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import { canAccessRoute } from '../utils/rolePermissions';
import { selfServeAllows, selfServeAllowsPage, selfServeRedirectFor } from '../utils/selfServe';
import useSelfServe from '../hooks/useSelfServe';

const Loading = () => (
    <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
    }}>
        <CircularProgress />
    </Box>
);

// The self-serve overlay as canAccessRoute's `allows`: the surfaces hidden from a member.
const selfServeMemberAllows = (permission) => selfServeAllows(permission, { viewer: true });

/**
 * Does the self-serve overlay close this route to a member (src/utils/selfServe.js)?
 * Either it is one of the API pages no permission gates (SELF_SERVE_HIDDEN_PAGES), or
 * the role reaches it only through a surface hidden from members
 * (SELF_SERVE_HIDDEN_PERMISSIONS: /admin/followups, /admin/companies, QuickShip's
 * /carriers...). A route the role cannot reach at all is not the overlay's to close.
 * Only says whether it would be closed to a member; the guards close it only once
 * useSelfServe says the viewer is one.
 */
export const closedToSelfServeMember = (userRole, path) =>
    !selfServeAllowsPage(path, { viewer: true }) ||
    (canAccessRoute(userRole, path) && !canAccessRoute(userRole, path, { allows: selfServeMemberAllows }));

// Version 5.0 - /admin is the universal home for EVERY authenticated role.
// All traffic routes here; the standalone /dashboard is retired. Superadmin has full
// access; every other role can always reach the admin shell + dashboard, and individual
// sub-routes are gated by that role's feature permissions (canAccessRoute). Role
// emulation is enforced via the effective userRole (which is the emulated role).
export const AdminRoute = ({ children }) => {
    const { currentUser, userRole, loading, initialized } = useAuth();
    const selfServe = useSelfServe();
    const location = useLocation();
    const currentPath = location.pathname;

    // Show loading spinner while checking auth state
    if (loading || !initialized) {
        return <Loading />;
    }

    // If not authenticated, redirect to login
    if (!currentUser) {
        return <Navigate to="/login" replace />;
    }

    // A Google session with no profile yet (needsSetup) has no company or role:
    // it can only finish its account.
    if (currentUser.needsSetup) {
        return <Navigate to="/signup/finish" replace />;
    }

    // Bounce accounts deactivated/suspended/soft-deleted mid-session (defense-in-depth
    // alongside AuthContext's force sign-out). Skipped during emulation, where the
    // effective user is synthetic and carries no status.
    if (currentUser.status === 'inactive' || currentUser.status === 'suspended' || currentUser.status === 'deleted' || currentUser.deleted === true) {
        return <Navigate to="/login" replace />;
    }

    // Superadmin always has full access (whether actual or emulated)
    if (userRole === 'superadmin') {
        return children;
    }

    // The admin home/dashboard is reachable by EVERY authenticated role — this is the
    // universal landing. We never bounce anyone to the retired /dashboard.
    const isAdminHome = currentPath === '/admin'
        || currentPath === '/admin/'
        || currentPath === '/admin/dashboard';
    if (isAdminHome) {
        return children;
    }

    // Gate specific admin sub-routes by the effective role's feature permissions.
    // Denied routes fall back to the admin home, not to /dashboard.
    if (!canAccessRoute(userRole, currentPath)) {
        console.log(`🚫 Role "${userRole}" cannot access ${currentPath} — redirecting to /admin home`);
        return <Navigate to="/admin" replace />;
    }

    // A route the role reaches only through a surface hidden from self-serve members
    // (src/utils/selfServe.js SELF_SERVE_HIDDEN_PERMISSIONS, e.g. /admin/followups), or an
    // API page (SELF_SERVE_HIDDEN_PAGES, e.g. /admin/api-keys), is closed to a member, whose
    // calls there the server refuses. Until the company context says whether this is a
    // member, render nothing that could make those calls.
    if (closedToSelfServeMember(userRole, currentPath)) {
        if (!selfServe.known) return <Loading />;
        if (selfServe.viewer) return <Navigate to={selfServeRedirectFor(currentPath)} replace />;
    }

    // Role has permission — allow access
    return children;
};
